import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { apiPost } from '../api/httpClient';
import menuItems from '../menu-items';

const fallbackModule = 'Aplicación';
const fallbackTitle = 'Pantalla';

const normalizePath = (value) => {
    if (!value) {
        return '/';
    }

    return value.endsWith('/') && value.length > 1 ? value.slice(0, -1) : value;
};

const buildRouteMap = (items) => {
    const routeMap = new Map();

    const visitNode = (node, currentModule) => {
        if (!node) {
            return;
        }

        const nextModule = node.type === 'collapse'
            ? node.title
            : currentModule || node.title || (node.id === 'dashboard' ? 'Dashboard' : fallbackModule);

        if (node.type === 'item' && node.url && !node.external) {
            routeMap.set(normalizePath(node.url), {
                module: currentModule || nextModule || fallbackModule,
                title: node.title || fallbackTitle
            });
        }

        (node.children || []).forEach((child) => visitNode(child, nextModule));
    };

    (items?.items || []).forEach((group) => {
        const groupModule = group.title || (group.id === 'dashboard' ? 'Dashboard' : undefined);
        (group.children || []).forEach((child) => visitNode(child, groupModule));
    });

    routeMap.set('/', { module: 'Dashboard', title: 'Inicio' });

    return routeMap;
};

const resolveRouteMetadata = (routeMap, pathname) => {
    const normalizedPath = normalizePath(pathname);

    if (routeMap.has(normalizedPath)) {
        return routeMap.get(normalizedPath);
    }

    const segments = normalizedPath.split('/').filter(Boolean);

    return {
        module: segments[0] || fallbackModule,
        title: segments[1] || fallbackTitle
    };
};

const RouteUsageTracker = () => {
    const location = useLocation();
    const routeMap = useMemo(() => buildRouteMap(menuItems), []);
    const lastTrackedPath = useRef('');

    useEffect(() => {
        const pathWithQuery = `${location.pathname}${location.search}`;

        if (lastTrackedPath.current === pathWithQuery) {
            return;
        }

        lastTrackedPath.current = pathWithQuery;

        const metadata = resolveRouteMetadata(routeMap, location.pathname);

        apiPost('/tracking/actions', {
            module: metadata.module,
            title: metadata.title,
            inputPayload: pathWithQuery,
            outputPayload: 'page-view'
        }).catch(() => {
            // Tracking must never interrupt navigation.
        });
    }, [location.pathname, location.search, routeMap]);

    return null;
};

export default RouteUsageTracker;
